import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { modelConfigsApi } from '@/services/api';
import type { CreateModelConfigInput, UpdateModelConfigInput, ModelConfigFilters } from '@/types';

export const useModelConfigs = (page = 1, limit = 20, filters?: ModelConfigFilters) => {
  return useQuery({
    queryKey: ['model-configs', page, limit, filters],
    queryFn: () => modelConfigsApi.getAll(page, limit, filters).then(res => res.data),
  });
};

export const useModelConfig = (id: string | undefined) => {
  return useQuery({
    queryKey: ['model-config', id],
    queryFn: () => modelConfigsApi.getById(id!).then(res => res.data.data),
    enabled: !!id,
  });
};

export const useModelConfigByName = (name: string | undefined) => {
  return useQuery({
    queryKey: ['model-config-name', name],
    queryFn: () => modelConfigsApi.getByName(name!).then(res => res.data.data),
    enabled: !!name,
  });
};

export const useCreateModelConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateModelConfigInput) =>
      modelConfigsApi.create(data).then(res => res.data.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model-configs'] });
    },
  });
};

export const useUpdateModelConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateModelConfigInput }) =>
      modelConfigsApi.update(id, data).then(res => res.data.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['model-configs'] });
      queryClient.invalidateQueries({ queryKey: ['model-config', variables.id] });
    },
  });
};

export const useDeleteModelConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => modelConfigsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model-configs'] });
    },
  });
};
